// @flow
import React, { Component } from 'react'
import PlusCircle from 'mz-icons/plus-circle'
import MinusCircle from 'mz-icons/minus-circle'
import BaseField from '../../core/BaseField'
import NormalizeValue from '../../core/NormalizeValue'
import Input from '../../core/Input'
import { Controls, InputControl } from './elements'
import { eventToKey, isMobileBrowser } from '../../utils'

// Types
import type {
  Props as BaseFieldProps,
  Event,
  Input as InputProps,
  InputRenderProps,
} from '../../core/BaseField'

export type Props = BaseFieldProps & {
  max?: number,
  min?: number,
  step: number,
}

class NumberField extends Component<Props> {
  static defaultProps = {
    step: 1,
  }

  inputRef = null

  updateSelection = false

  componentDidUpdate() {
    const {
      input: { value },
    } = this.props

    this.selectEnteredValue(value)
  }

  selectEnteredValue(value: ?number) {
    const selectionEnd = `${value || 0}`.length

    if (this.updateSelection && this.inputRef && !isMobileBrowser()) {
      this.inputRef.setSelectionRange(0, selectionEnd)

      this.updateSelection = false
    }
  }

  isMinValue = (value: number) => {
    const { min } = this.props
    return value == min // eslint-disable-line eqeqeq
  }

  isMaxValue = (value: number) => {
    const { max } = this.props
    return value == max // eslint-disable-line eqeqeq
  }

  isValueAboveMin = (value: number) => {
    const { min } = this.props
    return (!min && min !== 0) || value > min
  }

  isValueBelowMax = (value: number) => {
    const { max } = this.props
    return (!max && max !== 0) || value < max
  }

  isValueOnRange = (value: number) =>
    (this.isMinValue(value) || this.isValueAboveMin(value)) &&
    (this.isMaxValue(value) || this.isValueBelowMax(value))

  normalizeValue = (nextValue: string, value: string) => {
    const string = nextValue.replace(/[^\d]/g, '')
    const result = string === '' ? null : Number(string)
    return result === null || this.isValueOnRange(result) ? result : value
  }

  handleIncrement = () => {
    const {
      step,
      input: { onChange, value },
    } = this.props

    const processedValue = Number(value || 0)
    if (this.isValueBelowMax(processedValue)) {
      const newValue = processedValue + step
      this.updateSelection = true

      onChange && onChange(newValue)
    }
  }

  handleDecrement = () => {
    const {
      step,
      input: { onChange, value },
    } = this.props

    const processedValue = Number(value || 0)
    if (this.isValueAboveMin(processedValue)) {
      const newValue = processedValue - step
      this.updateSelection = true

      onChange && onChange(newValue)
    }
  }

  handleMouseDown = (event: any) => {
    event.preventDefault()
  }

  handleFocus = (input: InputProps) => () => {
    const { onFocus, value } = input

    this.updateSelection = true
    this.selectEnteredValue(value)

    onFocus && onFocus()
  }

  handleInputKeyDown = (event: Event) => {
    const key = eventToKey(event)

    if (key === 'ArrowUp') {
      this.handleIncrement()
      event.preventDefault()
    } else if (key === 'ArrowDown') {
      this.handleDecrement()
      event.preventDefault()
    }
  }

  renderInput = ({ input, ...props }: InputRenderProps) => (
    <NormalizeValue
      input={{
        ...input,
        onFocus: this.handleFocus(input),
        onKeyDown: this.handleInputKeyDown,
      }}
      normalizer={this.normalizeValue}
      render={(inputProps) => (
        <Input
          {...props}
          props={{
            ...props.props,
            type: isMobileBrowser() ? 'number' : 'text',
          }}
          innerRef={(c) => (this.inputRef = c)}
          input={inputProps}
        />
      )}
    />
  )

  renderControls = () => {
    const {
      min,
      max,
      input: { value },
    } = this.props

    const disableDecrement =
      (min || min === 0) && (value || value === 0) && value <= min

    const disableIncrement =
      (max || max === 0) && (value || value === 0) && value >= max

    return (
      <Controls>
        <InputControl
          $disabled={disableDecrement}
          onClick={this.handleDecrement}
          onMouseDown={this.handleMouseDown}
        >
          <MinusCircle />
        </InputControl>
        <InputControl
          $disabled={disableIncrement}
          onClick={this.handleIncrement}
          onMouseDown={this.handleMouseDown}
        >
          <PlusCircle />
        </InputControl>
      </Controls>
    )
  }

  render() {
    return (
      <BaseField
        {...this.props}
        renderInput={this.renderInput}
        renderPostfix={this.renderControls}
        cleanable={false}
      />
    )
  }
}

export default NumberField
